/* Staffing summary — the roll against the requirement sheet, department by
 * department and designation by designation; and, where the page shows
 * presence too, heads on the floor by shift, shift by day, and what the
 * month adds up to. */

import { Fragment } from 'react'
import { SK } from '../../lib/api_attendance'
import type { AttMonth, ShiftKey } from '../../lib/api_attendance'
import { PanelHead } from '../../components/ui'
import { num } from '../../lib/format'
import { otPerHead, shiftLabel } from './model'
import type { DesigScope, Scope } from './model'
import { BAD, Foot, OK, R, Sheet, Track, heads, listOf, pal, plural, signed, titleCase, words } from './parts'
import type { People } from './parts'

interface Props {
  D: AttMonth; S: Scope; G: DesigScope; target: number; dark: boolean; people: People
  /** False on the Staffing page, which is about the roll, not the day. */
  showPresence: boolean
  /** The departments of the By-designation table that are open. */
  open: Record<string, boolean>
  setOpen: (open: Record<string, boolean>) => void
}

export function StaffingTab(props: Props) {
  const { S, G, showPresence, people } = props
  return (
    <>
      {showPresence ? <ByShiftPanel {...props} /> : null}
      {showPresence ? <Coverage {...props} /> : null}
      {G.rows.length ? <ByDesignation {...props} /> : null}
      {/* On the staff view the By-designation table is already the staff
          table, department by department. */}
      {S.isAll && people === 'workers' ? <Staff {...props} /> : null}
      {S.isAll && showPresence ? <Findings {...props} /> : null}
    </>
  )
}

// ------------------------------------------------------------ by shift
function ByShiftPanel({ D, S, target, people }: Props) {
  const { avgShift, reqShift, isAll, sel } = S
  const staff = people === 'staff'
  const scale = Math.max(...SK.map((k) => Math.max(avgShift[k], reqShift[k]))) * 1.06 || 1
  const noShift = S.totNoShift / S.nDays

  const rows = SK.map((k) => {
    const r = reqShift[k]
    const g = r ? avgShift[k] - r : null
    const [name, starts] = D.shiftNames[k].split(' · ')
    return {
      k,
      label: k === 'O' ? '12-hr / other' : 'Shift ' + name,
      sub: k === 'O' ? '12-hour duty, other codes, no shift code' : 'starts ' + starts,
      avg: num(avgShift[k], 1),
      reqLabel: r ? ' / ' + num(r) : '',
      gap: g === null ? (isAll ? 'not sanctioned' : '') : signed(g, 1),
      gapColor: g === null ? 'var(--color-neutral-600)' : g < 0 ? BAD : OK,
      w: Math.round((avgShift[k] / scale) * 600),
      reqX: r ? Math.round((r / scale) * 600) : null,
      fill: r && (avgShift[k] / r) * 100 < target ? BAD : 'var(--c-bar1)',
    }
  })

  const sanctioned = SK.filter((k) => reqShift[k])
  const short = sanctioned.filter((k) => avgShift[k] < reqShift[k])
  const worst = sanctioned
    .map((k) => ({ k, pct: (avgShift[k] / reqShift[k]) * 100 }))
    .sort((a, b) => a.pct - b.pct)[0]
  const thin = SK.filter((k) => k !== 'O')
    .map((k) => ({ k, v: avgShift[k] }))
    .sort((a, b) => a.v - b.v)[0]

  const unknown = noShift >= 0.05
    ? ` ${num(noShift, 1)} of them a day are ${people} the ${staff ? 'report' : 'register'} marks present with no shift code on the biometric${staff ? '' : ' — nearly all punch misses —'} so they belong to one of the shifts above, and the gaps there are overstated by that much.`
    : ''
  const note = isAll && worst
    ? (short.length === sanctioned.length ? 'Every sanctioned shift runs short. '
        : short.length ? `${short.length} of the ${sanctioned.length} sanctioned shifts run short. `
          : 'No sanctioned shift runs short on average. ')
      + `Shift ${worst.k} is the thinnest at ${num(worst.pct, 0)}% of strength`
      + (reqShift[worst.k] > avgShift[worst.k]
        ? ` — ${num(reqShift[worst.k] - avgShift[worst.k], 0)} heads missing on an average ${worst.k === 'C' ? 'night' : 'day'}. `
        : '. ')
      + `A further ${num(avgShift.O, 1)} heads a day sit under 12-hour duty and other codes, which the requirement sheet does not budget as a shift; they cover part of the gap, on twelve-hour days.`
      + unknown
    : `${sel} puts ${num(S.presentAvg, 1)} heads on the floor a day across ${heads(S.activeRoll, people)} on the active roll. Its thinnest shift is ${thin.k} at ${num(thin.v, 1)} heads`
      + (avgShift.O > 0.2 ? `, with ${num(avgShift.O, 1)} a day under 12-hour duty and other codes` : '')
      + '. '
      + (S.req
        ? `The requirement sheet asks for ${num(S.req)} heads here — ${num(reqShift.A)} on A, ${num(reqShift.B)} on B, ${num(reqShift.C)} on C and ${num(reqShift.G)} on general — and the ticks mark them.`
        : 'The requirement sheet does not name this department, so there is nothing to read the bars against.')
      + unknown

  return (
    <Sheet>
      <PanelHead title="Manning against requirement, by shift" hint={isAll
        ? 'Average heads present per day · tick is the sanctioned strength'
        : S.req
          ? `Average heads present per day · tick is what the sheet asks of ${sel.toLowerCase()}`
          : 'Average heads present per day · no sanctioned strength for this department'} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>
        {rows.map((s) => (
          <div key={s.k} style={{
            display: 'grid', gridTemplateColumns: '170px minmax(0,1fr) 200px',
            gap: 16, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, lineHeight: 1.1 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{s.sub}</div>
            </div>
            <Track width={600} height={22} value={s.w} fill={s.fill} tick={s.reqX} />
            <div className="num" style={{
              fontSize: 13, display: 'flex', gap: 14, justifyContent: 'flex-end',
            }}>
              <span>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: 20 }}>{s.avg}</span>
                <span style={{ color: 'var(--color-neutral-600)' }}>{s.reqLabel}</span>
              </span>
              <span style={{ minWidth: 64, textAlign: 'right', color: s.gapColor }}>{s.gap}</span>
            </div>
          </div>
        ))}
      </div>
      <Foot>{note}</Foot>
    </Sheet>
  )
}

// ------------------------------------------------------- shift × day
function Coverage({ D, S, target, dark, people }: Props) {
  const P = pal(dark)
  const { sc, reqShift, isAll, sel } = S
  const heatMax = Math.max(1, ...sc.map((x) => Math.max(...SK.map((k) => x.s[k]))))
  // With a requirement the grid shows the sanctioned shifts only; for one
  // department there is none, so every row is shown against its own best.
  const keys = SK.filter((k) => (isAll ? reqShift[k] : true))

  const weak: Record<string, number> = {}
  const rows = keys.map((k) => ({
    k,
    cells: sc.map((d) => {
      const v = d.s[k]
      const pct = reqShift[k] ? (v / reqShift[k]) * 100 : (v / heatMax) * 100
      const under = pct < target
      if (under) weak[k] = (weak[k] ?? 0) + 1
      return {
        d: d.d,
        txt: num(v),
        title: `${d.d} ${D.mon} · shift ${k} · ${num(v)}`
          + (reqShift[k] ? ` of ${reqShift[k]} (${num(pct, 0)}%)` : ' heads'),
        bg: under ? P.covWeak : pct < 100 ? P.covMid : P.covStrong,
        fg: under ? P.covWeakFg : pct < 100 ? P.covMidFg : P.covStrongFg,
      }
    }),
  }))
  const weakCells = Object.values(weak).reduce((a, b) => a + b, 0)
  const weakest = (Object.keys(weak) as ShiftKey[]).sort((a, b) => weak[b] - weak[a])[0]
  const cols = `140px repeat(${sc.length}, minmax(24px,1fr))`

  return (
    <Sheet>
      <PanelHead title="Shift × day coverage" right={
        <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
          {isAll
            ? `Palest cells = below ${num(target)}% of sanctioned strength`
            : `Fill scales against ${sel.toLowerCase()}’s best-manned shift-day`}
        </div>
      } />
      <div className="scroll-x" style={{ marginTop: 18 }}>
        <div style={{ minWidth: 900 }}>
          <div className="num" style={{
            display: 'grid', gridTemplateColumns: cols, gap: 3, fontSize: 11,
            color: 'var(--color-neutral-600)', marginBottom: 6,
          }}>
            <div />
            {sc.map((d) => <div key={d.d} style={{ textAlign: 'center' }}>{d.d}</div>)}
          </div>
          {rows.map((row) => (
            <div key={row.k} style={{
              display: 'grid', gridTemplateColumns: cols, gap: 3, marginBottom: 3,
            }}>
              <div style={{
                fontSize: 14, color: 'var(--color-neutral-800)', whiteSpace: 'nowrap',
                paddingRight: 8, display: 'flex', alignItems: 'center',
              }}>{shiftLabel(row.k)}</div>
              {row.cells.map((c) => (
                <div key={c.d} title={c.title} className="num" style={{
                  height: 42, background: c.bg, color: c.fg, fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{c.txt}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <Foot top={14} wide={false}>
        {isAll
          ? `Cell shows heads present. Fill deepens with coverage — the palest cells are shifts below ${num(target)}% of sanctioned strength: `
            + (weakCells
              ? `${num(weakCells)} of the ${num(keys.length * sc.length)} shift-days shown, most often shift ${weakest}.`
              : 'none of the shift-days shown.')
            + ` ${people === 'staff' ? 'Staff' : 'Workers'} present with no shift code on the biometric are in none of these rows.`
          : `${sel} only. Cell shows heads present; with no per-shift sanction for a single department, the fill scales against the department’s own best-manned shift-day (${num(heatMax)} heads).`}
      </Foot>
    </Sheet>
  )
}

// ----------------------------------------------------- by designation
/** Each department's sheet lines, folded under the department's total.
 *  Under `pct` of 95 the cover reads red. */
const cover = (req: number, roll: number) => {
  const gap = roll - req
  const pct = req ? (roll / req) * 100 : 0
  return {
    gap: req ? signed(gap) : '—',
    gapColor: !req ? 'var(--color-neutral-600)' : gap < -0.5 ? BAD : OK,
    pct: num(pct, 0) + '%',
    w: Math.min(pct, 100).toFixed(0) + '%',
    fill: pct < 95 ? BAD : 'var(--c-bar1)',
  }
}

function ByDesignation({ S, G, people, open, setOpen }: Props) {
  const { isAll } = S
  // The sheet has no designations for staff: each department is one line,
  // so the table is flat — nothing to fold open.
  const flat = people === 'staff'
  const anyOpen = G.depts.some((d) => open[d])
  const depts = G.depts.map((dn) => {
    const xs = G.rows.filter((x) => x.dept === dn)
    const req = xs.reduce((s, x) => s + x.req, 0)
    const roll = xs.reduce((s, x) => s + x.roll, 0)
    // One department in view is already open; its row has nothing to fold.
    return { dn, xs, req, roll, open: !flat && (!isAll || !!open[dn]), ...cover(req, roll) }
  })
  const noReq = <span className="tag tag-outline" style={{ fontSize: 10 }}>not in requirement sheet</span>

  return (
    <Sheet>
      <PanelHead title={flat ? 'By department' : 'By designation'} right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
            {G.rows.length ? `${num(G.short)} short · ${num(G.over)} over` : ''}
          </span>
          {flat ? null : (
            <button className="btn btn-ghost no-print" type="button"
              style={{ fontSize: 12, padding: '5px 10px' }}
              onClick={() => setOpen(anyOpen ? {} : Object.fromEntries(G.depts.map((d) => [d, true])))}>
              {anyOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
        </div>
      } />
      <div className="scroll-x" style={{ marginTop: 16 }}>
        <table className="table" style={{ width: '100%', minWidth: 760 }}>
          <thead>
            <tr>
              <th>{flat ? 'Department' : 'Designation'}</th><th style={R}>Required</th><th style={R}>On roll</th>
              <th style={R}>Gap</th><th style={{ width: '22%' }}>Cover</th>
            </tr>
          </thead>
          <tbody>
            {depts.map((g) => (
              <Fragment key={g.dn}>
                <tr className={flat ? undefined : 'mcrow clickable'} aria-expanded={flat ? undefined : g.open}
                  onClick={flat ? undefined : () => setOpen({ ...open, [g.dn]: !open[g.dn] })}
                  style={g.open ? { background: 'var(--color-accent-100)' } : undefined}>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {flat ? null : <span className="caret" aria-hidden="true">{g.open ? '▾' : '▸'}</span>}
                    {g.dn}
                    {flat ? null : (
                      <span style={{
                        fontWeight: 400, fontSize: 11, color: 'var(--color-neutral-600)', marginLeft: 8,
                      }}>{plural(g.xs.length, 'designation')}</span>
                    )}
                  </td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{g.req ? num(g.req) : '—'}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{num(g.roll)}</td>
                  <td className="num" style={{ ...R, fontWeight: 600, color: g.gapColor }}>{g.gap}</td>
                  <td>
                    {g.req ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ position: 'relative', flex: 1, height: 8, background: 'var(--c-track)' }}>
                          <div style={{
                            position: 'absolute', left: 0, top: 0, bottom: 0, width: g.w, background: g.fill,
                          }} />
                        </div>
                        <span className="num" style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{g.pct}</span>
                      </div>
                    ) : noReq}
                  </td>
                </tr>
                {g.open ? g.xs.map((x) => {
                  const c = cover(x.req, x.roll)
                  return (
                    <tr key={x.desig}>
                      <td style={{ whiteSpace: 'nowrap', paddingLeft: 34, color: 'var(--color-neutral-800)' }}>
                        {titleCase(x.desig)}
                      </td>
                      <td className="num" style={R}>{x.req ? num(x.req) : '—'}</td>
                      <td className="num" style={R}>{num(x.roll)}</td>
                      <td className="num" style={{ ...R, color: c.gapColor }}>{c.gap}</td>
                      <td>
                        {x.req ? (
                          <div className="num" style={{ fontSize: 12, textAlign: 'center', color: c.gapColor }}>{c.pct}</div>
                        ) : noReq}
                      </td>
                    </tr>
                  )
                }) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <Foot top={14}>
        {flat
          ? '"On roll" counts everyone coded to that department with at least one present day in the month, compared with the requirement sheet\'s staff line for the same department — the sheet gives staff by department and shift only, with no designations. Attendance and daily presence live on the Attendance page.'
          : '"On roll" counts everyone coded to that department and designation with at least one present day in the month, compared with the requirement sheet line for the same department and designation. Attendance and daily presence live on the Attendance page. A designation over strength next to one short in the same department usually means people are coded to the wrong designation.'}
      </Foot>
    </Sheet>
  )
}

// ------------------------------------------------------------- staff
/** Staff against their own requirement. Not in the design — its sheet had
 *  workers only — but the requirement sheet now carries a staff table, and
 *  this is the one place it is read. */
function Staff({ D }: Props) {
  const T = D.staff
  if (!T.required && !T.onRoll) return null
  const gap = T.avgPresent - T.required
  const extra = T.depts.filter((d) => d.req === null).map((d) => d.name)
  const r = T.reqByShift
  return (
    <Sheet>
      <PanelHead title="Staff against requirement"
        hint={`${num(T.required)} required · ${num(T.onRoll)} on the roll · ${num(T.avgPresent, 1)} present on an average day`} />
      <table className="table" style={{ marginTop: 16, width: '100%' }}>
        <thead>
          <tr>
            <th>Department</th><th style={R}>Present/day</th><th style={R}>Required</th>
            <th style={R}>Gap</th><th style={R}>On roll</th>
          </tr>
        </thead>
        <tbody>
          {T.depts.map((d) => {
            const g = d.req === null ? null : d.avgPresent - d.req
            return (
              <tr key={d.name}>
                <td style={{ whiteSpace: 'nowrap' }}>{d.name}</td>
                <td className="num" style={R}>{num(d.avgPresent, 1)}</td>
                <td className="num" style={{ ...R, color: 'var(--color-neutral-700)' }}>
                  {d.req === null ? '—' : num(d.req)}
                </td>
                <td className="num" style={{ ...R, color: g === null ? 'var(--color-neutral-600)' : g < 0 ? BAD : OK }}>
                  {g === null ? '—' : signed(g, 1)}
                </td>
                <td className="num" style={{ ...R, color: 'var(--color-neutral-700)' }}>{num(d.onRoll)}</td>
              </tr>
            )
          })}
          <tr>
            <td style={{ fontWeight: 600 }}>All staff</td>
            <td className="num" style={{ ...R, fontWeight: 600 }}>{num(T.avgPresent, 1)}</td>
            <td className="num" style={{ ...R, fontWeight: 600 }}>{num(T.required)}</td>
            <td className="num" style={{ ...R, fontWeight: 600, color: gap < 0 ? BAD : OK }}>{signed(gap, 1)}</td>
            <td className="num" style={{ ...R, fontWeight: 600 }}>{num(T.onRoll)}</td>
          </tr>
        </tbody>
      </table>
      <Foot top={16}>
        Staff are kept out of every other figure on this page. The sheet asks for {num(r.A ?? 0)} on
        shift A, {num(r.B ?? 0)} on B, {num(r.C ?? 0)} on C and {num(r.G ?? 0)} on general. Staff have no
        register, so present is the biometric report’s own word for the day, over
        the {plural(T.dayCount, 'day')} it covers.
        {extra.length
          ? ` ${listOf(extra)} ${extra.length === 1 ? 'is' : 'are'} on the staff roll but not on the sheet.`
          : ''}
      </Foot>
    </Sheet>
  )
}

// ---------------------------------------------------------- findings
function Findings({ D, S, people }: Props) {
  const W = words(people)
  // The month's own story: whole period, whatever the day range says.
  const days = D.days
  const present = days.map((d) => d.present)
  const manPct = (D.avgPresent / D.requiredTotal) * 100
  const shortPct = 100 - manPct
  const atStrength = present.filter((p) => p >= D.requiredTotal).length
  const week = (list: number[]) => list.reduce((s, v) => s + v, 0) / (list.length || 1)
  const w1 = week(present.slice(0, 7)), w4 = week(present.slice(-7))
  const drift = w4 - w1
  const enough = days.length >= 14
  const latePct = D.totals.presentDays ? (D.totals.late / D.totals.presentDays) * 100 : 0
  const otTop = D.depts.filter((d) => d.ot > 0).sort((a, b) => b.ot - a.ot).slice(0, 2)
    .map((d) => d.name.toLowerCase())
  const otHead = D.depts.filter((d) => d.ot > 0).sort((a, b) => otPerHead(b) - otPerHead(a))[0]

  const findings = [
    {
      title: shortPct >= 1
        ? `The plant runs ${num(shortPct, 0)}% under strength` + (atStrength === 0 ? ', every day' : '')
        : 'The plant is manned to its sanctioned strength',
      tag: 'manning', tagCls: 'tag-accent',
      text: `Against ${num(D.requiredTotal)} sanctioned heads the floor averages ${num(D.avgPresent, 0)}. `
        + (atStrength === 0
          ? `Not one day in ${D.monthName} reached strength; `
          : `${num(atStrength)} of ${num(days.length)} days reached strength; `)
        + `the best day was ${num(Math.max(...present))} heads and the worst ${num(Math.min(...present))}.`,
      stat: num(manPct, 0) + '%', statNote: 'of sanctioned strength',
    },
    {
      title: !enough ? 'Too few days on file to read a trend'
        : drift <= -1 ? 'Strength is drifting down through the month'
          : drift >= 1 ? 'Strength rose through the month'
            : 'Strength held level through the month',
      tag: drift < 0 ? 'worsening' : 'stable', tagCls: drift < 0 ? 'tag-accent' : 'tag-neutral',
      text: `First seven days averaged ${num(w1, 0)} heads, last seven ${num(w4, 0)}. `
        + (!enough ? 'With under a fortnight loaded the two weeks overlap.'
          : Math.abs(drift) >= 1
            ? `A ${num(Math.abs(drift), 0)}-head ${drift < 0 ? 'slide' : 'rise'} inside one month. The attendance data carries no holiday calendar, so set it against festivals and leave before reading it as a trend.`
            : 'No slide or build-up inside the month.'),
      stat: signed(drift, 0), statNote: 'heads, first week → last',
    },
    {
      title: `${num(latePct, 0)}% of present days are flagged late`,
      tag: 'punctuality', tagCls: 'tag-outline',
      text: `${num(D.totals.late)} of ${num(D.totals.presentDays)} present days carry a LATE flag. `
        + (latePct >= 20
          ? 'At that rate the flag has stopped meaning anything — either the grace period is set too tight in the biometric software, or shift start times in the master do not match how the shifts actually run.'
          : `That is low enough for the flag to mean something; the names are on the ${W.Many} tab.`),
      stat: num(latePct, 0) + '%', statNote: 'of present days',
    },
    // Staff have no PP marks, so there is no overtime to read.
    ...(people === 'staff' ? [] : [{
      title: shortPct >= 1 && D.totals.ot > 0
        ? 'Twelve-hour days are covering part of the shortfall'
        : 'Twelve-hour days in the month',
      tag: 'cost', tagCls: 'tag-accent',
      text: `${num(D.totals.ot)} overtime hours in ${D.monthName}, roughly ${num(D.totals.ot / days.length, 0)} hours a day`
        + (otTop.length ? `, concentrated in ${listOf(otTop)}` : '')
        + `. The reports book no overtime, so this is read off the register: ${D.otHours.PP} hours for each PP mark, ${D.otHours.PPP} for a PPP.`
        + (otHead ? ` Heaviest per head in ${otHead.name.toLowerCase()}, at ${num(otPerHead(otHead), 0)} hours a ${W.one}.` : '')
        + (shortPct >= 1 && D.totals.ot > 0 ? ' That is missing headcount being covered by longer days.' : ''),
      stat: num(D.totals.ot), statNote: 'OT hrs in the month',
    }]),
  ]

  return (
    <Sheet>
      <PanelHead title="What the month says"
        hint={S.fullRange ? undefined : 'Whole period, not date-filtered'} />
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: 14 }}>
        {findings.map((f, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '38px minmax(0,1fr) 150px', gap: 18,
            alignItems: 'start', padding: '16px 0', borderTop: '1px solid var(--color-divider)',
          }}>
            <div style={{
              fontFamily: 'var(--font-heading)', fontSize: 26, lineHeight: 1,
              color: 'var(--color-neutral-500)',
            }}>{String(i + 1).padStart(2, '0')}</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: 19 }}>{f.title}</span>
                <span className={`tag ${f.tagCls}`}>{f.tag}</span>
              </div>
              <div className="pretty" style={{
                fontSize: 14, lineHeight: 1.5, color: 'var(--color-neutral-800)',
                marginTop: 6, maxWidth: '88ch',
              }}>{f.text}</div>
            </div>
            <div className="num" style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 24, lineHeight: 1 }}>{f.stat}</div>
              <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{f.statNote}</div>
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  )
}
